import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const auth = token => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} });

// -- staff -------------------------------------------------------------------

export const listUsers = token => axios.get(`${API}/users`, auth(token)).then(r => r.data);

/** `pin` is write-only: the server stores a bcrypt hash and never returns it. */
export const createUser = (body, token) =>
  axios.post(`${API}/users`, body, auth(token)).then(r => r.data);

export const updateUser = (userId, patch, token) =>
  axios.put(`${API}/users/${userId}`, patch, auth(token)).then(r => r.data);

/** Deactivates rather than erasing — a user id is referenced by every order they took. */
export const deleteUser = (userId, token) =>
  axios.delete(`${API}/users/${userId}`, auth(token)).then(r => r.data);

/** Remove an inactive account for good (2026-10-20). Its row stays for the bills and
 * the audit log; it leaves the list, never signs in again, and its ID is free. */
export const removeUser = (userId, token) =>
  axios.post(`${API}/users/${userId}/remove`, null, auth(token)).then(r => r.data);

/** Sign every active device out so duty starts cleanly at each person's next sign-in (2026-11-02). */
export const signOutEveryone = token =>
  axios.post(`${API}/users/sign-out-everyone`, null, auth(token)).then(r => r.data);

export const regeneratePin = (userId, token) =>
  axios.post(`${API}/users/${userId}/pin/reset`, null, auth(token)).then(r => r.data);

export const resetPin = regeneratePin;

export const setCustomPin = (userId, pin, token) =>
  axios.put(`${API}/users/${userId}`, { pin }, auth(token)).then(r => r.data);

export const unlockPin = (userId, token) =>
  axios.post(`${API}/users/${userId}/pin/unlock`, null, auth(token)).then(r => r.data);

// -- permissions -------------------------------------------------------------

// Custom staff roles (2026-10-31): a named role on admin / cashier / captain.
export const listStaffRoles = token => axios.get(`${API}/staff-roles`, auth(token)).then(r => r.data);
export const createStaffRole = (body, token) => axios.post(`${API}/staff-roles`, body, auth(token)).then(r => r.data);
export const updateStaffRole = (id, patch, token) =>
  axios.patch(`${API}/staff-roles/${id}`, patch, auth(token)).then(r => r.data);
export const deleteStaffRole = (id, moveTo, token) =>
  axios.delete(`${API}/staff-roles/${id}`, { ...auth(token), params: moveTo ? { move_to: moveTo } : {} });

export const permissionCatalog = token =>
  axios.get(`${API}/permissions/catalog`, auth(token)).then(r => r.data);

export const rolePermissions = (role, token) =>
  axios.get(`${API}/permissions/roles/${role}`, auth(token)).then(r => r.data);

export const saveRolePermissions = (role, body, token) =>
  axios.put(`${API}/permissions/roles/${role}`, body, auth(token)).then(r => r.data);

// -- menu --------------------------------------------------------------------

/**
 * Full menu as categories carrying their items.
 *
 * There is no flat GET /menu/items — admin needs `available_only=false` so 86'd items
 * still appear and can be switched back on. The bootstrap payload is the flattened
 * captain-facing view and deliberately omits what admin has to edit.
 */
export const listMenu = token =>
  axios.get(`${API}/menu`, { ...auth(token), params: { available_only: false } }).then(r => r.data);

export const createMenuItem = (body, token) =>
  axios.post(`${API}/menu/items`, body, auth(token)).then(r => r.data);

export const updateMenuItem = (itemId, patch, token) =>
  axios.put(`${API}/menu/items/${itemId}`, patch, auth(token)).then(r => r.data);

/** The menu hub's view: inactive categories included, so they can be switched back on. */
export const listMenuWithInactive = token =>
  axios
    .get(`${API}/menu`, { ...auth(token), params: { available_only: false, include_inactive: true } })
    .then(r => r.data);

export const createCategory = (body, token) =>
  axios.post(`${API}/menu/categories`, body, auth(token)).then(r => r.data);

/** Rename, re-sort, regroup, or reactivate (`is_active: true`) — one PUT for all of it. */
export const updateCategory = (categoryId, patch, token) =>
  axios.put(`${API}/menu/categories/${categoryId}`, patch, auth(token)).then(r => r.data);

/** Soft delete: items reference the category, so the server deactivates it. */
export const deactivateCategory = (categoryId, token) =>
  axios.delete(`${API}/menu/categories/${categoryId}`, auth(token)).then(r => r.data);

export const listGroups = token =>
  axios.get(`${API}/menu/groups`, auth(token)).then(r => r.data);

export const createGroup = (body, token) =>
  axios.post(`${API}/menu/groups`, body, auth(token)).then(r => r.data);

export const updateGroup = (groupId, patch, token) =>
  axios.put(`${API}/menu/groups/${groupId}`, patch, auth(token)).then(r => r.data);

export const deactivateGroup = (groupId, token) =>
  axios.delete(`${API}/menu/groups/${groupId}`, auth(token)).then(r => r.data);

/** Master groups: Food / Beverages / Extras, the top of the menu (menu_group_categories). */
export const listMasterGroups = token =>
  axios.get(`${API}/menu/master-groups`, auth(token)).then(r => r.data);

export const createMasterGroup = (body, token) =>
  axios.post(`${API}/menu/master-groups`, body, auth(token)).then(r => r.data);

export const updateMasterGroup = (masterId, patch, token) =>
  axios.put(`${API}/menu/master-groups/${masterId}`, patch, auth(token)).then(r => r.data);

export const deactivateMasterGroup = (masterId, token) =>
  axios.delete(`${API}/menu/master-groups/${masterId}`, auth(token)).then(r => r.data);

export const createVariation = (itemId, body, token) =>
  axios.post(`${API}/menu/items/${itemId}/variations`, body, auth(token)).then(r => r.data);

export const updateVariation = (itemId, variationId, patch, token) =>
  axios.put(`${API}/menu/items/${itemId}/variations/${variationId}`, patch, auth(token)).then(r => r.data);

export const deleteVariation = (itemId, variationId, token) =>
  axios.delete(`${API}/menu/items/${itemId}/variations/${variationId}`, auth(token)).then(r => r.data);

/** One person's permission view: effective, their overrides, and the role's own set. */
export const userPermissions = (userId, token) =>
  axios.get(`${API}/permissions/users/${userId}`, auth(token)).then(r => r.data);

/** Full replace of one person's overrides: { key: "allow" | "deny" }. */
export const setUserPermissions = (userId, overrides, token) =>
  axios.put(`${API}/permissions/users/${userId}`, { overrides }, auth(token)).then(r => r.data);

/** Every table (an admin is never restricted to a section). */
export const listTables = token => axios.get(`${API}/tables`, auth(token)).then(r => r.data);

// ── Order lists (settings sweep X5, 2026-10-19) ───────────────────────────────
export const listPaymentModes = token => axios.get(`${API}/payment-modes`, auth(token)).then(r => r.data);
export const createPaymentMode = (body, token) => axios.post(`${API}/payment-modes`, body, auth(token)).then(r => r.data);
export const updatePaymentMode = (id, body, token) => axios.put(`${API}/payment-modes/${id}`, body, auth(token)).then(r => r.data);

export const listCancellationReasonsAll = token =>
  axios.get(`${API}/cancellation-reasons`, auth(token)).then(r => r.data);
export const createCancellationReason = (body, token) =>
  axios.post(`${API}/cancellation-reasons`, body, auth(token)).then(r => r.data);
export const updateCancellationReason = (id, body, token) =>
  axios.put(`${API}/cancellation-reasons/${id}`, body, auth(token)).then(r => r.data);

// Ready-made special notes for order lines (2026-10-20). Every role reads them;
// writing needs menu.edit.
export const listNotePresetsAll = token =>
  axios.get(`${API}/item-note-presets`, auth(token)).then(r => r.data);
export const createNotePreset = (body, token) =>
  axios.post(`${API}/item-note-presets`, body, auth(token)).then(r => r.data);
export const updateNotePreset = (id, body, token) =>
  axios.put(`${API}/item-note-presets/${id}`, body, auth(token)).then(r => r.data);

// A menu item's photo — the top of the item details view. Answers the item.
export const uploadMenuItemImage = (itemId, blob, filename, token) => {
  const form = new FormData();
  form.append("file", blob, filename || "photo.jpg");
  return axios.post(`${API}/menu/items/${itemId}/image`, form, auth(token)).then(r => r.data);
};
export const removeMenuItemImage = (itemId, token) =>
  axios.delete(`${API}/menu/items/${itemId}/image`, auth(token)).then(r => r.data);

export const listOrderTypesAll = token => axios.get(`${API}/order-types`, auth(token)).then(r => r.data);
export const createOrderType = (body, token) => axios.post(`${API}/order-types`, body, auth(token)).then(r => r.data);
export const updateOrderType = (id, body, token) => axios.put(`${API}/order-types/${id}`, body, auth(token)).then(r => r.data);

export const listTransitions = token => axios.get(`${API}/admin/workflow/transitions`, auth(token)).then(r => r.data);
export const patchTransition = (id, body, token) =>
  axios.patch(`${API}/admin/workflow/transitions/${id}`, body, auth(token)).then(r => r.data);
